import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Navbar } from '@/components/navbar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Check, Sparkles, Zap, Crown, ArrowRight, Loader2 } from 'lucide-react'
import { subscriptionApi } from '@/services/api'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { isAuthenticated } from '@/lib/utils'

// Icon mapping for plans
const planIcons = {
  'Free': Sparkles,
  'Basic': Zap,
  'Premium': Crown,
}

// Color mapping for plans
const planColors = {
  'Free': 'from-gray-400 to-gray-500',
  'Basic': 'from-slate-600 to-slate-700',
  'Premium': 'from-slate-700 to-slate-800',
}

function PricingCard({ plan, onSubscribe, loading, savings }) {
  const Icon = planIcons[plan.name] || Sparkles
  const color = planColors[plan.name] || 'from-gray-400 to-gray-500'
  const displayPrice = parseFloat(plan.price || 0).toFixed(2)
  const isYearly = plan.interval === 'yearly'
  const displayInterval = plan.interval || 'month'

  // Parse features from API response
  const features = plan.features?.map(f => f.inline_feature) || []

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative rounded-2xl border-2 p-8 ${plan.selected
          ? 'border-primary shadow-xl scale-105'
          : 'border-gray-200 shadow-sm'
        } bg-white`}
    >
      {plan.selected && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <Badge data-logo-box className="text-white px-4 py-1 transition-all duration-300">
            Most Popular
          </Badge>
        </div>
      )}

      <div className="mb-6">
        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${color} mb-4`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
        <p className="text-gray-600 text-sm">{plan.description || `Perfect for ${plan.name.toLowerCase()} users`}</p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-5xl font-bold text-gray-900">
            ${displayPrice}
          </span>
          <span className="text-gray-500">/{displayInterval}</span>
        </div>
        {isYearly && savings && (
          <p className="text-sm text-green-600 font-medium mt-2">
            Save {savings.percent}% (${savings.savings}/year)
          </p>
        )}
        {isYearly && !savings && (
          <p className="text-sm text-green-600 font-medium mt-2">
            Billed annually
          </p>
        )}
      </div>

      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={() => onSubscribe(plan.alias)}
        disabled={loading}
        data-logo-box={plan.selected ? true : undefined}
        className="w-full transition-all duration-300"
        size="lg"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing...
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {plan.price === '0.00' || plan.price === 0 ? 'Get Started' : 'Subscribe'}
            <ArrowRight className="w-4 h-4" />
          </div>
        )}
      </Button>
    </motion.div>
  )
}

export function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [loading, setLoading] = useState(false)
  const [loadingPayment, setLoadingPayment] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Fetch available plans from API
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await subscriptionApi.getPlans()
      return response.data
    },
    onError: () => {
      toast.error('Failed to load pricing plans')
    }
  })

  // Payment verification after redirect from Stripe
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const sessionId = searchParams.get('session_id')

    if (!sessionId) return

    setLoadingPayment(true)
    let attempts = 0
    let interval

    const verifyPayment = async () => {
      attempts += 1

      try {
        const verifyResponse = await subscriptionApi.verifyPayment(sessionId)

        if (verifyResponse.data.paid === true) {
          // Payment verified, check subscription status
          const subscriptionResponse = await subscriptionApi.getCurrentSubscription()

          clearInterval(interval)
          setLoadingPayment(false)

          if (subscriptionResponse.data.feeds?.length === 0) {
            toast.success('Payment successful! Please select your feeds.')
            navigate('/dashboard/feed/')
            return
          }

          if (
            subscriptionResponse.data.feeds?.length >= 1 &&
            subscriptionResponse.data.has_access &&
            subscriptionResponse.data.is_completed
          ) {
            toast.success('Payment successful! Subscription activated.')
            const redirectPath = localStorage.getItem('ldirect')
            localStorage.removeItem('ldirect')
            navigate(redirectPath || '/')
            return
          }

          toast.success('Payment successful!')
          navigate('/')
        }
      } catch (error) {
        console.error('Payment verification error:', error)

        if (error.response?.status === 401) {
          clearInterval(interval)
          setLoadingPayment(false)
          localStorage.setItem('ldirect', window.location.href)
          toast.error('Please login to continue')
          navigate('/login')
          return
        }
      }

      // Stop after 5 attempts (50 seconds)
      if (attempts >= 5) {
        clearInterval(interval)
        setLoadingPayment(false)
        toast.error('Payment verification timed out. Please contact support if payment was deducted.')
      }
    }

    // Poll every 10 seconds
    interval = setInterval(verifyPayment, 10000)
    // Also check immediately
    verifyPayment()

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [location.search, navigate])

  // Filter and sort plans by billing cycle
  const sortPlansWithSelectedInMiddle = (plans) => {
    if (!plans || plans.length === 0) return []

    // Sort by price first
    const sorted = [...plans].sort((a, b) => parseFloat(a.price) - parseFloat(b.price))

    // Find the selected plan
    const selectedIndex = sorted.findIndex(p => p.selected)
    if (selectedIndex === -1) return sorted

    // Rearrange to put selected in middle
    const result = []
    const selectedPlan = sorted[selectedIndex]
    const otherPlans = sorted.filter((_, i) => i !== selectedIndex)

    // For 3 plans: [0, selected, 1]
    // For 2 plans: [0, selected] or [selected, 0]
    if (otherPlans.length === 2) {
      result.push(otherPlans[0], selectedPlan, otherPlans[1])
    } else if (otherPlans.length === 1) {
      result.push(otherPlans[0], selectedPlan)
    } else {
      result.push(selectedPlan, ...otherPlans)
    }

    return result
  }

  const monthlyPlans = sortPlansWithSelectedInMiddle(plansData?.filter(p => p.interval === 'monthly'))
  const yearlyPlans = sortPlansWithSelectedInMiddle(plansData?.filter(p => p.interval === 'yearly'))

  const visiblePlans = billingCycle === 'monthly' ? monthlyPlans : yearlyPlans

  // Calculate savings for yearly plans
  const calculateSavings = (yearlyPlan) => {
    if (!yearlyPlan || yearlyPlan.interval !== 'yearly') return null
    // Find corresponding monthly plan with same name
    // Note: Plans are distinguished by alias (UUID), but we match by name for savings calculation
    const monthlyPlan = monthlyPlans?.find(p => p.name === yearlyPlan.name)
    if (!monthlyPlan) return null

    const yearlyPrice = parseFloat(yearlyPlan.price)
    const monthlyPrice = parseFloat(monthlyPlan.price)
    const monthlyYearlyEquivalent = monthlyPrice * 12
    const savings = monthlyYearlyEquivalent - yearlyPrice
    const savingsPercent = ((savings / monthlyYearlyEquivalent) * 100).toFixed(0)

    return { savings: savings.toFixed(2), percent: savingsPercent }
  }

  const handleSubscribe = async (planAlias) => {
    // Find the plan to check if it's free
    const plan = visiblePlans.find(p => p.alias === planAlias)
    if (plan && (plan.price === '0.00' || plan.price === 0 || plan.name === 'Free')) {
      navigate('/signup')
      return
    }

    // Check if user is authenticated before making API call
    if (!isAuthenticated()) {
      localStorage.setItem('ldirect', '/pricing')
      toast.error('Please login to subscribe')
      navigate('/login')
      return
    }

    setLoading(true)
    try {
      const response = await subscriptionApi.initPayment(planAlias)
      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url
      } else if (response.data.has_access) {
        toast.success('Subscription activated!')
        navigate('/dashboard')
      } else {
        toast.success('Subscription activated!')
        navigate('/dashboard')
      }
    } catch (error) {
      console.error('Subscription error:', error)

      if (error.response?.status === 401) {
        // Token might be expired - redirect to login
        localStorage.setItem('ldirect', '/pricing')
        toast.error('Session expired. Please login again.')
        navigate('/login')
        return
      } else if (error.response?.status === 400) {
        // Bad request - show specific error message
        const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Invalid request'
        toast.error(errorMsg)
      } else {
        toast.error('Failed to start subscription. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Show loading state
  if (plansLoading || loadingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-gray-600">
              {loadingPayment ? 'Verifying your payment...' : 'Loading pricing plans...'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Choose Your <span data-logo-text className="bg-clip-text text-transparent transition-all duration-300">Perfect Plan</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get unlimited access to quality news from trusted sources worldwide
          </p>
        </motion.div>

        {/* Billing Toggle */}
        {yearlyPlans.length > 0 && (
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'
              }`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-8 w-14 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'
                  }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'
              }`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <Badge className="bg-green-100 text-green-700">
                Save More
              </Badge>
            )}
          </div>
        )}

        {/* Pricing Cards - Centered when less than 3 plans */}
        <div className={`grid gap-8 mb-16 p-5 ${visiblePlans.length === 1
            ? 'grid-cols-1 max-w-md mx-auto'
            : visiblePlans.length === 2
              ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'
              : 'grid-cols-1 md:grid-cols-3'
          }`}>
          {visiblePlans.length > 0 ? (
            visiblePlans.map((plan) => (
              <PricingCard
                key={plan.alias}
                plan={plan}
                onSubscribe={handleSubscribe}
                loading={loading}
                savings={calculateSavings(plan)}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600">No pricing plans available at the moment.</p>
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'Can I cancel anytime?',
                a: 'Yes! You can cancel your subscription at any time. Your access will continue until the end of your billing period.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards (Visa, Mastercard, American Express) and PayPal.',
              },
              {
                q: 'Is there a free trial?',
                a: 'Yes! All paid plans come with a 7-day free trial. Cancel anytime during the trial period at no charge.',
              },
              {
                q: 'Can I switch plans later?',
                a: 'Absolutely! You can upgrade or downgrade your plan at any time from your dashboard.',
              },
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  )
}
